// @ts-strict-ignore
import {ParamComponent} from 'panel';
import produce from 'immer';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {TemplateSpecs, TemplateType} from 'common/report/dashboards/types';
import {EntrySectionUpdates} from 'common/query-builder/query-entry-section/types';

import css from './index.inline.styl';
import {template} from './template';
import {
  ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP,
  RHSInputType,
  RHSPanelParams,
  RHSPanelProps,
  RHSPanelState,
  TemplateField,
  TutorialConfig,
} from './types';

export class RHSPanel extends ParamComponent<RHSPanelParams, RHSPanelState, IronContextRegistry> {
  // START: copied from boards-template-modal
  readonly TEMPLATE_SELECTION_STEP = 0;
  readonly EVENT_SELECTION_STEP = 1;
  readonly WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;
  readonly WHY_THESE_EVENTS_URL = `https://docs.mixpanel.com/docs/boards/templates#adding-your-events`;
  readonly CART_ANALYSIS_URL = `https://docs.mixpanel.com/docs/features/advanced#list-of-objects-property-support`;
  readonly VALUE_MOMENT_URL = `https://docs.mixpanel.com/docs/quickstart/what-to-track#step-2-track-a-value-moment-event`;

  hasRequiredFields = (requiredFields: TemplateField[]): boolean => {
    return requiredFields.every((field) => !!this.state.fields[field]);
  };

  readonly TEMPLATE_CONFIGS = Object.keys(TemplateSpecs).reduce((acc, key) => {
    const {requiredFields, ...rest} = TemplateSpecs[key];
    return {
      ...acc,
      [key]: {
        ...rest,
        requiredFieldsCheck: () => this.hasRequiredFields(requiredFields),
      },
    };
  }, {});

  // END: copied from boards-template-modal
  get config() {
    return {
      contexts: [`currentProject` as const, `i18n` as const, `themes` as const],
      css,
      defaultState: {
        fields: {},
        calendarInputOpen: false,
        tutorialCurrentStepIndex: 0,
        showTutorial: false,
        hideTutorial: false,
      },
      helpers: {
        getNoDataEventString: (): string => {
          switch (this.params.selectedTemplate) {
            case TemplateType.KPI:
            case TemplateType.FEATURE_LAUNCH:
              return `just two events`;
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return `just one event`;
            default:
              return `some events`;
          }
        },
        getInfoEventString: (): string => {
          switch (this.params.selectedTemplate) {
            case TemplateType.E_COMMERCE:
              return this.t(`boards.rhs.setup.ecommerce`);
            case TemplateType.MARKETING_KPI:
            case TemplateType.KPI:
            case TemplateType.BENCHMARK_KPI:
              return this.t(`boards.rhs.setup.two-events`);
            case TemplateType.FEATURE_LAUNCH:
              return this.t(`boards.rhs.setup.two-events-details`);
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return this.t(`boards.rhs.setup.one-event`);
            default:
              return this.t(`boards.rhs.setup.some-events`);
          }
        },
        hasIntegratedArb: (): boolean | undefined => this.getContext(`currentProject`).get()?.value?.hasIntegratedArb,
        updateField: (value: any, eventName: TemplateField, inputType: RHSInputType): void => {
          this.update(
            produce(this.state, (draft) => {
              draft.fields[eventName] = value;
            }),
          );
          this.helpers.setTutorialStepIndex();
          this.dispatchEvent(new CustomEvent(`updateBlueprint`, {detail: {eventName, eventValue: value, inputType}}));
        },
        removeField: (eventName: TemplateField): void => {
          this.update(
            produce(this.state, (draft) => {
              delete draft.fields[eventName];
            }),
          );
        },
        shouldDisableFinishButton: (): boolean | undefined => {
          return !this.TEMPLATE_CONFIGS[this.params.selectedTemplate]?.requiredFieldsCheck() || this.params.isLoading;
        },
        createTemplateBoard: () => {
          if (this.helpers.shouldDisableFinishButton()) {
            return;
          }
          this.dispatchEvent(new CustomEvent(`commitBlueprint`));
        },
        startTutorial: (): void => {
          this.update({showTutorial: true});
        },
        dismissTutorial: (): void => {
          this.update({hideTutorial: true});
        },
        getTemplateTutorialConfig: (): TutorialConfig => {
          return ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP.get(this.params?.selectedTemplate);
        },
        setTutorialStepIndex: (): void => {
          const tutorial = this.helpers.getTemplateTutorialConfig();
          const {tutorialCurrentStepIndex} = this.state;
          if (tutorial) {
            let stepIndex = tutorialCurrentStepIndex;
            let [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            while (this.state.fields[templateField]) {
              // check each tutorial entry to see if it's field wasn't already filled out
              stepIndex = templateStep.nextStepIndex;
              [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            }
            this.update({tutorialCurrentStepIndex: stepIndex});
          }
        },
        handleEventUpdate: (updates: EntrySectionUpdates, eventName: TemplateField) => {
          if (updates.entries.length) {
            this.helpers.updateField(updates, eventName, RHSInputType.Event);
          } else {
            this.helpers.removeField(eventName);
          }
        },
        handleSeeAllTemplatesClick: () => {
          this.dispatchEvent(new CustomEvent(`clickSeeAllTemplates`, {detail: {fields: this.state.fields}}));
        },
        shouldShowReminderTooltip: () => {
          return this.params.showReminderTooltip;
        },
      },
      useShadowDom: true,
      params: {
        mode: String,
        selectedTemplate: String,
        isLoading: Boolean,
        isSeeAllTemplatesLoading: Boolean,
        showReminderTooltip: Boolean,
      },
      template,
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }
}

export const RHSPanelEl = defineAndMakeJsxFactory<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
export const RHSPanelReact = panel2react<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
